// @pages/document/DocumentDetailsPrompt.page.tsx

import React, { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { renderRoute, routeReviewWithDocument } from "@navigation/typedRoutes";
import type { ContentError, Markdown } from "@ps-bridge/models/core/dataContracts";
import type { DocumentID } from "@ps-bridge/models/brands/documentID";
import type { DocumentAnswers } from "@ps-bridge/models/brands/documentAnswers";
import { useContentClient } from '@contexts/ContentClient.context';
import { useDocumentContext } from '@contexts/Document.context';
import { useLoadingController } from '@contexts/Loading.context';
import * as Option from "@utils/Option";
import { makeMarkdown } from "@ps-bridge/models/brands/markdown";
import { getParamOption } from "@utils/searchParams";
import { ok, type Ok } from "@ps-bridge/ok";
import { mkNavigate, mkRouteBuilder, mkTaskRunner } from "@ps-bridge/capabilities";
import type { AnswerDocumentQuestionsFetch } from "@ps-bridge/contracts/documentDetailsPrompt";
import type { TaskTs } from "@ps-bridge/interop";
import { ioEffect, taskEitherFromEffect } from '@utils/effects/branded';
import type { DocumentDetailsPromptKernel } from "@ps-bridge/contracts/documentDetailsPrompt";
import { buildDocumentDetailsPromptViewProps } from "@ps-bridge/documentDetailsPrompt.program";
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import DocumentDetailsPromptPagePanel, {
    type DocumentDetailsPromptPagePhase,
} from "@view/panels/document/DocumentDetailsPromptPage.panel";

const DocumentDetailsPromptPage: React.FC = () => {
    const navigate = useNavigate();
    const contentClient = useContentClient();
    const [searchParams] = useSearchParams();
    const probingQuestionValue = useMemo(
        () => getParamOption(searchParams, "question"),
        [searchParams],
    );
    const probingQuestion: Markdown = useMemo(
        () => Option.fold(probingQuestionValue, () => makeMarkdown(""), (value) => makeMarkdown(value)),
        [probingQuestionValue],
    );
    const { document } = useDocumentContext();
    const documentOption = document.read;
    const loading = useLoadingController();
    const runTaskWithError = useRunTaskWithError();
    const runTaskWithErrorIo = useCallback(
        (task: TaskTs<Ok>) =>
            ioEffect(() => {
                runTaskWithError(task).then(() => ok);
                return ok;
            }),
        [runTaskWithError],
    );
    const taskRunner = useMemo(() => mkTaskRunner(runTaskWithErrorIo), [runTaskWithErrorIo]);
    const answerDocumentQuestionsFetch: AnswerDocumentQuestionsFetch = useMemo(() => {
        const invoke = (documentId: DocumentID, request: { answers: DocumentAnswers }) =>
            taskEitherFromEffect(
                contentClient.answerDocumentQuestions(documentId, request),
                (reason) => reason as ContentError,
            );

        function fetch(documentId: DocumentID, request: { answers: DocumentAnswers }): ReturnType<typeof invoke>;
        function fetch(documentId: DocumentID): (request: { answers: DocumentAnswers }) => ReturnType<typeof invoke>;
        function fetch(documentId: DocumentID, request?: { answers: DocumentAnswers }) {
            if (request === undefined) {
                return (nextRequest: { answers: DocumentAnswers }) => invoke(documentId, nextRequest);
            }
            return invoke(documentId, request);
        }

        return Object.assign(fetch, {
            __brandAnswerDocumentQuestionsFetch: "AnswerDocumentQuestionsFetch" as const,
        }) as AnswerDocumentQuestionsFetch;
    }, [contentClient]);
    const kernel: DocumentDetailsPromptKernel = useMemo(
        () => ({
            loading,
            navigate: mkNavigate((route) =>
                ioEffect(() => {
                    navigate(renderRoute(route), { replace: true });
                    return ok;
                }),
            ),
            reviewRoute: mkRouteBuilder(routeReviewWithDocument),
            document,
            answerDocumentQuestions: answerDocumentQuestionsFetch,
            taskRunner,
        }),
        [answerDocumentQuestionsFetch, document, loading, navigate, taskRunner],
    );
    const viewProps = useMemo(
        () =>
            buildDocumentDetailsPromptViewProps({
                kernel,
                documentQuestion: probingQuestion,
            }),
        [kernel, probingQuestion],
    );

    const phase: DocumentDetailsPromptPagePhase = Option.fold(
        documentOption,
        () => "missing",
        () => "ready",
    );

    return <DocumentDetailsPromptPagePanel phase={phase} viewProps={viewProps} />;
};

export default DocumentDetailsPromptPage;
